import React, { useState } from 'react';
import {
  BookOpen,
  ChevronDown,
  ChevronUp,
  Dumbbell,
  BarChart3,
  BrainCircuit,
  Target,
  Gauge,
  Zap,
  Star,
  Flame,
  Activity,
} from 'lucide-react';

// ── Collapsible section ──────────────────────

function Section({
  icon,
  title,
  defaultOpen = false,
  children,
}: {
  icon: React.ReactNode;
  title: string;
  defaultOpen?: boolean;
  children: React.ReactNode;
}) {
  const [open, setOpen] = useState(defaultOpen);
  return (
    <div className="bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-700 rounded-xl overflow-hidden">
      <button
        onClick={() => setOpen(!open)}
        className="w-full flex items-center gap-3 px-5 py-4 text-left hover:bg-slate-50 dark:hover:bg-slate-800/50 transition-colors"
      >
        <div className="flex-shrink-0 w-8 h-8 rounded-lg bg-brand-50 dark:bg-brand-950 flex items-center justify-center text-brand-600">
          {icon}
        </div>
        <span className="flex-1 font-bold text-sm">{title}</span>
        {open ? (
          <ChevronUp size={16} className="text-slate-400" />
        ) : (
          <ChevronDown size={16} className="text-slate-400" />
        )}
      </button>
      {open && <div className="px-5 pb-5 space-y-3 text-sm text-slate-600 dark:text-slate-300">{children}</div>}
    </div>
  );
}

// ── Term definition row ──────────────────────

function Term({ term, children }: { term: string; children: React.ReactNode }) {
  return (
    <div className="flex gap-3 items-start">
      <span className="flex-shrink-0 min-w-[130px] font-bold text-slate-800 dark:text-slate-100 text-xs uppercase tracking-wide mt-0.5">
        {term}
      </span>
      <span className="text-xs leading-relaxed">{children}</span>
    </div>
  );
}

function Divider() {
  return <hr className="border-slate-100 dark:border-slate-800" />;
}

// ── Main component ───────────────────────────

export default function LegendPage() {
  return (
    <div className="max-w-3xl mx-auto space-y-4 animate-fade-in">
      {/* Header */}
      <div className="text-center mb-6">
        <div className="inline-flex items-center justify-center w-14 h-14 rounded-2xl bg-brand-100 dark:bg-brand-950 mb-3">
          <BookOpen size={28} className="text-brand-600" />
        </div>
        <h1 className="text-2xl font-bold tracking-tight mb-1">Legenda i Poradnik</h1>
        <p className="text-sm text-slate-500 dark:text-slate-400">
          Kompletny przewodnik po wszystkich skrótach, metrykach i pojęciach używanych w APEX Builder.
          <br />
          Napisany prostym językiem - nawet dla początkujących.
        </p>
      </div>

      {/* ═══════════════════════════════════════
          1. PODSTAWOWE POJĘCIA TRENINGOWE
          ═══════════════════════════════════════ */}
      <Section icon={<Dumbbell size={18} />} title="Podstawowe pojęcia treningowe" defaultOpen>
        <Term term="Serie (Sets)">
          Seria to jedno ciągłe wykonanie danego ćwiczenia (np. 10 przysiadów bez przerwy = 1 seria).
          Jeśli masz zapisane „4 serie", oznacza to, że wykonasz ćwiczenie 4 razy z przerwami między seriami.
        </Term>
        <Divider />
        <Term term="Powtórzenia (Reps)">
          Liczba pojedynczych ruchów w jednej serii. „8–12" oznacza, że w jednej serii wykonujesz od 8 do
          12 powtórzeń. Jeśli widzisz „AMRAP", znaczy to: wykonaj jak najwięcej powtórzeń do końca serii.
        </Term>
        <Divider />
        <Term term="Tempo (np. 3-1-2-0)">
          Cztery cyfry opisujące prędkość ruchu w sekundach:
          <br />
          <b>1. faza ekscentryczna</b> (opuszczanie ciężaru) - <b>2. pauza na dole</b> - <b>3. faza koncentryczna</b> (podnoszenie) - <b>4. pauza na górze</b>.
          <br />
          Tempo „3-1-2-0" = opuszczaj 3s, pauza 1s, podnoś 2s, bez pauzy na górze.
        </Term>
        <Divider />
        <Term term="Odpoczynek">
          Czas przerwy między seriami (w sekundach lub minutach). Np. „90s" = 90 sekund, „2m" = 2 minuty.
          Dłuższe przerwy (2–4 min) sprzyjają treningowi siłowemu; krótsze (30–90s) - hipertrofii.
        </Term>
        <Divider />
        <Term term="Złożone (Compound)">
          Ćwiczenia angażujące wiele stawów i grup mięśniowych jednocześnie.
          Przykłady: przysiad, wyciskanie sztangi, martwy ciąg, wiosłowanie.
          Tworzą fundament każdego programu treningowego.
        </Term>
        <Divider />
        <Term term="Izolacja (Isolation)">
          Ćwiczenia angażujące głównie jeden staw i jedną grupę mięśniową.
          Przykłady: uginanie bicepsa, prostowanie tricepsa, odwodzenie ramion.
          Służą do dopracowania poszczególnych partii.
        </Term>
        <Divider />
        <Term term="Akcesoryjne (Accessory)">
          Ćwiczenia wspomagające - mniejsze ruchy, które wspierają ćwiczenia główne
          lub wzmacniają słabe punkty. Często łączone z izolacją.
        </Term>
        <Divider />
        <Term term="Rozgrzewka (Warmup)">
          Ćwiczenia przygotowujące ciało do treningu - aktywacja mięśni, mobilność,
          dynamiczne rozciąganie, foam rolling. Wykonywane przed częścią główną treningu
          z niską intensywnością (RPE 3-5).
        </Term>
      </Section>

      {/* ═══════════════════════════════════════
          2. OBCIĄŻENIE I INTENSYWNOŚĆ
          ═══════════════════════════════════════ */}
      <Section icon={<Gauge size={18} />} title="Obciążenie i intensywność - RPE, %1RM">
        <Term term="RPE">
          <b>Rating of Perceived Exertion</b> - skala subiektywnej trudności od 1 do 10.
          <br />
          • <b>RPE 6</b> = mógłbyś zrobić jeszcze ~4 powtórzenia (łatwo)
          <br />
          • <b>RPE 7</b> = zostały ~3 powtórzenia w zapasie
          <br />
          • <b>RPE 8</b> = zostały ~2 powtórzenia (ciężko)
          <br />
          • <b>RPE 9</b> = zostało ~1 powtórzenie (bardzo ciężko)
          <br />
          • <b>RPE 10</b> = absolutne maximum, żadnego zapasu
          <br />
          Im wyższe RPE, tym większa intensywność treningu.
        </Term>
        <Divider />
        <Term term="%1RM">
          Procent <b>jednopowtórzeniowego maksimum</b> (1RM) - najcięższego ciężaru, jaki możesz podnieść
          raz w danym ćwiczeniu.
          <br />
          • 70%1RM = 70% tego maksymalnego ciężaru.
          <br />
          • Zakres 65–75% = typowy dla hipertrofii, 80–90% = siła, 90%+ = moc/max.
        </Term>
        <Divider />
        <Term term="Konwersja RPE → %1RM">
          Aplikacja przelicza RPE na przybliżony %1RM za pomocą tabeli Helmsa:
          <br />
          <i>%1RM ≈ 100 − (10 − RPE) × 2.5 − (powtórzenia − 1) × 2.5</i>
          <br />
          Np. RPE 8 przy 8 powt. ≈ 100 − 2×2.5 − 7×2.5 = 77.5% 1RM.
        </Term>
        <Divider />
        <Term term="Strefy intensywności">
          <b>Niska (≤RPE 6)</b> - rozgrzewka, technika
          <br />
          <b>Umiarkowana (RPE 7–7.5)</b> - większość pracy hipertroficznej
          <br />
          <b>Wysoka (RPE 8–8.5)</b> - ciężka praca, blisko limitów
          <br />
          <b>Maksymalna (RPE ≥9)</b> - testy siłowe, ostatnie serie
        </Term>
      </Section>

      {/* ═══════════════════════════════════════
          3. OBJĘTOŚĆ TRENINGOWA - MEV, MAV, MRV
          ═══════════════════════════════════════ */}
      <Section icon={<BarChart3 size={18} />} title="Objętość treningowa - MEV, MAV, MRV">
        <p className="text-xs text-slate-500 dark:text-slate-400 italic">
          To najważniejsze metryki w analityce - określają, ile serii na daną partię mięśniową
          powinieneś robić tygodniowo, żeby rosnąć, ale się nie przetrenować.
        </p>
        <Divider />
        <Term term="MEV">
          <b>Minimum Effective Volume</b> - minimalna objętość skuteczna.
          Najmniejsza liczba serii tygodniowo na daną partię, żeby w ogóle wzbudzić adaptację (wzrost mięśni).
          <br />
          Poniżej MEV → za mało treningu, praktycznie brak postępów.
        </Term>
        <Divider />
        <Term term="MAV">
          <b>Maximum Adaptive Volume</b> - optymalna objętość.
          Zakres, w którym uzyskujesz najlepszy stosunek wysiłku do efektu.
          <br />
          Dla większości osób to najlepszy cel - tu rosną mięśnie najszybciej.
        </Term>
        <Divider />
        <Term term="MRV">
          <b>Maximum Recoverable Volume</b> - maksymalna objętość, z której twoje ciało
          jest jeszcze w stanie się zregenerować.
          <br />
          Powyżej MRV → przetrenowanie, spadek siły, ryzyko kontuzji.
        </Term>
        <Divider />
        <Term term="Statusy objętości">
          Kolory na wykresach oznaczają:
          <br />
          <span className="inline-block w-3 h-3 rounded-sm mr-1 align-middle" style={{ backgroundColor: '#6366f1' }} />
          <b>Poniżej MEV</b> - za mało pracy, dodaj serie
          <br />
          <span className="inline-block w-3 h-3 rounded-sm mr-1 align-middle" style={{ backgroundColor: '#3b82f6' }} />
          <b>Na MEV</b> - minimum, utrzymasz poziom
          <br />
          <span className="inline-block w-3 h-3 rounded-sm mr-1 align-middle" style={{ backgroundColor: '#22c55e' }} />
          <b>Optymalnie (MAV)</b> - idealny zakres, najlepsze efekty
          <br />
          <span className="inline-block w-3 h-3 rounded-sm mr-1 align-middle" style={{ backgroundColor: '#f59e0b' }} />
          <b>Blisko MRV</b> - dużo objętości, monitoruj zmęczenie
          <br />
          <span className="inline-block w-3 h-3 rounded-sm mr-1 align-middle" style={{ backgroundColor: '#ef4444' }} />
          <b>Nad MRV</b> - za dużo! Zmniejsz objętość, grozi przetrenowaniem
        </Term>
        <Divider />
        <Term term="Serie bezpośrednie / pośrednie">
          <b>Bezpośrednie</b> = serie, w których dany mięsień jest głównym celem ćwiczenia
          (np. serie wyciskania → bezpośrednio klatka piersiowa).
          <br />
          <b>Pośrednie</b> = serie, w których mięsień pracuje jako wspomagający
          (np. w wyciskaniu → pośrednio triceps i przód barków).
        </Term>
        <Divider />
        <Term term="Objętość obciążenia (Volume Load)">
          Skumulowana miara całkowitej pracy w treningu.
          <br />
          <i>Objętość obciążenia = Σ (serie × powtórzenia × intensywność)</i>
          <br />
          Im wyższa wartość, tym więcej „pracy mechanicznej" wykonano. Służy do porównywania
          tygodni i śledzenia progresji.
        </Term>
      </Section>

      {/* ═══════════════════════════════════════
          4. WZORCE RUCHOWE
          ═══════════════════════════════════════ */}
      <Section icon={<Activity size={18} />} title="Wzorce ruchowe (Movement Patterns)">
        <p className="text-xs text-slate-500 dark:text-slate-400 italic">
          Każde ćwiczenie w bazie jest sklasyfikowane według wzorca ruchowego.
          Dobry program powinien pokrywać wszystkie wzorce, żeby zapewnić równomierny rozwój.
        </p>
        <Divider />
        <Term term="Przysiad (Squat)">
          Ruchy zginania kolan i bioder jednocześnie. Np. przysiad ze sztangą, przysiad goblet, hack squat.
          Główne mięśnie: czworogłowy, pośladki.
        </Term>
        <Divider />
        <Term term="Martwy ciąg (Hinge)">
          Ruchy z dominacją bioder - pochylanie się do przodu przy prostych (-ish) kolanach.
          Np. martwy ciąg, rumunski martwy ciąg, hip thrust. Główne: dwugłowy, pośladki.
        </Term>
        <Divider />
        <Term term="Pchnięcie poziome">
          Odpychanie ciężaru od klatki piersiowej. Np. wyciskanie sztangi leżąc, pompki, wyciskanie hantli.
          Główne: klatka piersiowa, triceps, przód barków.
        </Term>
        <Divider />
        <Term term="Pchnięcie pionowe">
          Wyciskanie ciężaru nad głowę. Np. OHP (wyciskanie sztangi stojąc), wyciskanie hantli siedząc.
          Główne: barki, triceps.
        </Term>
        <Divider />
        <Term term="Ciągnięcie poziome">
          Przyciąganie ciężaru do tułowia. Np. wiosłowanie sztangą, wiosłowanie hantlem, seated row.
          Główne: górny grzbiet, najszersze, biceps.
        </Term>
        <Divider />
        <Term term="Ciągnięcie pionowe">
          Przyciąganie ciężaru z góry na dół. Np. podciąganie, ściąganie drążka.
          Główne: najszersze, biceps.
        </Term>
        <Divider />
        <Term term="Przenoszenie (Carry)">
          Ruchy z obciążeniem w rękach przy chodzeniu. Np. farmer's walk, spacer z kettlebell.
          Główne: chwyt, core, trapez.
        </Term>
        <Divider />
        <Term term="Brzuch/Core">
          Ćwiczenia stabilizacyjne i na mięśnie tułowia. Np. plank, wznosy nóg, ab rollout.
        </Term>
        <Divider />
        <Term term="Izolacja (góra/dół)">
          Ćwiczenia jednostawowe celujące w konkretny mięsień górnej lub dolnej połowy ciała.
          Np. uginanie bicepsa (góra), prostowanie nóg (dół).
        </Term>
      </Section>

      {/* ═══════════════════════════════════════
          5. ZMĘCZENIE I REGENERACJA
          ═══════════════════════════════════════ */}
      <Section icon={<Flame size={18} />} title="Zmęczenie i regeneracja">
        <Term term="Indeks zmęczenia">
          Skala 0–100 szacująca, jak zmęczony jest twój organizm po danym tygodniu treningowym.
          <br />
          Obliczany na podstawie: intensywności (RPE), liczby serii, proporcji ćwiczeń złożonych,
          częstotliwości sesji i tolerancji zmęczenia (podanej w konfiguracji planu).
          <br />
          <b>0–30</b> = niskie zmęczenie (zielone)
          <br />
          <b>31–55</b> = umiarkowane (żółto-zielone)
          <br />
          <b>56–75</b> = wysokie (pomarańczowe) - obserwuj
          <br />
          <b>76–100</b> = bardzo wysokie (czerwone) - zmniejsz obciążenie!
        </Term>
        <Divider />
        <Term term="Indeks regeneracji">
          Odwrotność zmęczenia - % zdolności regeneracyjnej, jaki ci pozostaje.
          Im wyższy, tym lepiej. Poniżej 40% → ryzyko przetrenowania.
        </Term>
        <Divider />
        <Term term="Tolerancja zmęczenia">
          Ustawienie w konfiguracji planu (1–10). Określa, jak dobrze „znosisz" duży wolumen treningu.
          Osoby bardziej zaawansowane i młodsze mają wyższą tolerancję.
          Wpływa na obliczenia indeksu zmęczenia.
        </Term>
        <Divider />
        <Term term="Deload">
          Tydzień o zmniejszonej objętości i intensywności (np. 50–60% normalnej pracy).
          Służy regeneracji i zapobieganiu przetrenowaniu.
          Zwykle planuje się go co 4–6 tygodni lub kiedy zmęczenie przekroczy 70%.
        </Term>
      </Section>

      {/* ═══════════════════════════════════════
          6. MODELE PROGRESJI
          ═══════════════════════════════════════ */}
      <Section icon={<TrendingUp size={18} />} title="Modele progresji">
        <p className="text-xs text-slate-500 dark:text-slate-400 italic">
          Model progresji określa, w jaki sposób zwiększasz obciążenie z tygodnia na tydzień.
          Każdy model ma inne zastosowanie.
        </p>
        <Divider />
        <Term term="Progresja liniowa (Linear)">
          Co tydzień dodajesz trochę ciężaru lub powtórzeń w sposób stały i przewidywalny.
          <br />
          Np. tydzień 1: 60 kg × 8, tydzień 2: 62.5 kg × 8, tydzień 3: 65 kg × 8...
          <br />
          <b>Dla kogo?</b> Początkujący - bo organizm szybko się adaptuje.
        </Term>
        <Divider />
        <Term term="Falowanie dzienne - DUP">
          <b>Daily Undulating Periodization</b> - intensywność i objętość zmieniają się w ramach
          jednego tygodnia (np. poniedziałek ciężej, środa lżej, piątek średnio).
          <br />
          <b>Dla kogo?</b> Średniozaawansowani i zaawansowani - daje więcej bodźców.
        </Term>
        <Divider />
        <Term term="Periodyzacja blokowa (Block)">
          Mezocykl dzieli się na bloki z różnym celem:
          <br />
          • Blok 1: objętość (dużo serii, umiarkowane ciężary)
          <br />
          • Blok 2: siła (mniej serii, cięższe ciężary)
          <br />
          • Blok 3: szczyt / test
          <br />
          <b>Dla kogo?</b> Zaawansowani, szczególnie w sportach siłowych.
        </Term>
        <Divider />
        <Term term="Autoregulacja (RPE)">
          Obciążenie dobierane „na czuć" według skali RPE - nie ustala się z góry ciężaru,
          tylko docelowe RPE (np. „pracuj do RPE 8"). Ciało samo reguluje intensywność
          w zależności od samopoczucia.
          <br />
          <b>Dla kogo?</b> Osoby doświadczone, umiejące rzetelnie ocenić swoje RPE.
        </Term>
      </Section>

      {/* ═══════════════════════════════════════
          7. CELE TRENINGOWE
          ═══════════════════════════════════════ */}
      <Section icon={<Target size={18} />} title="Cele treningowe">
        <Term term="Hipertrofia">
          Wzrost masy mięśniowej. Typowa objętość: 10–20 serii/mięsień/tydz., RPE 7–9,
          powtórzenia 6–15, tempo kontrolowane, przerwy 60–120s.
        </Term>
        <Divider />
        <Term term="Siła">
          Wzrost maksymalnej siły (1RM). Mniej serii (3–6), więcej przerw (3–5 min),
          cięższe ciężary (80–95% 1RM), niższe powtórzenia (1–5).
        </Term>
        <Divider />
        <Term term="Moc (Power)">
          Szybkość generowania siły. Ćwiczenia wybuchowe (np. zarzuty, szarpnięcia,
          przysiad ze skokiem). Niskie RPE, niskie powtórzenia, długie przerwy.
        </Term>
        <Divider />
        <Term term="Wytrzymałość">
          Zdolność do długiego wysiłku. Wysokie powtórzenia (15–30+), krótkie przerwy (30–60s),
          niższe obciążenia.
        </Term>
        <Divider />
        <Term term="Ogólna sprawność">
          Mieszanka różnych celów - trochę siły, trochę hipertrofii, trochę wytrzymałości.
          Idealne na start lub dla osób nienastawionych na jeden cel.
        </Term>
      </Section>

      {/* ═══════════════════════════════════════
          8. OCENA JAKOŚCI PLANU
          ═══════════════════════════════════════ */}
      <Section icon={<Star size={18} />} title="Ocena jakości planu (0–100)">
        <p className="text-xs text-slate-500 dark:text-slate-400 italic">
          System automatycznie ocenia jakość twojego programu w 5 wymiarach i daje ocenę 0–100.
        </p>
        <Divider />
        <Term term="Balans Push/Pull">
          Czy masz równowagę między ruchami „pchania" (wyciskanie, pompki) a „ciągnięcia" (wiosłowanie, podciąganie)?
          Nierównowaga prowadzi do wad postawy i kontuzji.
        </Term>
        <Divider />
        <Term term="Pokrycie ruchów">
          Czy program obejmuje wszystkie wzorce ruchowe (przysiad, martwy ciąg, pchnięcia, ciągnięcia, core)?
          Braki = słabe punkty w rozwoju.
        </Term>
        <Divider />
        <Term term="Zarządzanie zmęczeniem">
          Czy program nie generuje za dużo zmęczenia? Czy jest zaplanowany deload?
          Wysoki indeks zmęczenia bez deloadu → niska ocena.
        </Term>
        <Divider />
        <Term term="Rozkład objętości">
          Czy objętość treningowa na poszczególne mięśnie jest w dobrym zakresie (MEV–MRV)?
          Za mało lub za dużo = niższa ocena.
        </Term>
        <Divider />
        <Term term="Logika progresji">
          Czy wybrany model progresji pasuje do poziomu trenującego i celu?
          Np. progresja liniowa dla zaawansowanego = niska ocena.
        </Term>
        <Divider />
        <Term term="Oceny literowe">
          <b>A+ (90–100)</b> - doskonały program
          <br />
          <b>A (80–89)</b> - bardzo dobry
          <br />
          <b>B (70–79)</b> - dobry, drobne poprawki
          <br />
          <b>C (60–69)</b> - przeciętny, kilka problemów
          <br />
          <b>D (50–59)</b> - słaby, wymaga zmian
          <br />
          <b>F (&lt;50)</b> - poważne problemy, przeprojektuj
        </Term>
      </Section>

      {/* ═══════════════════════════════════════
          9. MEZOCYKL I STRUKTURA PLANU
          ═══════════════════════════════════════ */}
      <Section icon={<Zap size={18} />} title="Mezocykl i struktura planu">
        <Term term="Mezocykl">
          Blok treningowy trwający zazwyczaj 4–8 tygodni, po którym następuje zmiana planu
          lub deload. Jest to podstawowa jednostka planowania treningowego.
        </Term>
        <Divider />
        <Term term="Częstotliwość (×/tydz.)">
          Ile razy w tygodniu trenujesz. Np. 4×/tydz. = 4 sesje treningowe tygodniowo.
          Każda partia mięśniowa powinna być trenowana min. 2× w tygodniu dla optymalnych efektów.
        </Term>
        <Divider />
        <Term term="Sesja treningowa">
          Jeden trening danego dnia. Ma przypisany dzień, fokus (np. „Góra ciała", „Nogi") i listę ćwiczeń.
        </Term>
        <Divider />
        <Term term="Fokus sesji">
          Główny cel sesji, np. „Push A", „Pull B", „Nogi", „Cała góra". Pomaga organizować
          ćwiczenia tematycznie i zapewnić pokrycie wszystkich partii.
        </Term>
        <Divider />
        <Term term="Tydzień / Week">
          Jeden tydzień treningowy w ramach mezocyklu. Każdy tydzień może mieć inną konfigurację
          (np. ostatni tydzień = deload). W analityce każdy tydzień analizowany jest osobno.
        </Term>
      </Section>

      {/* ═══════════════════════════════════════
          10. MAPA MIĘŚNI I NAZWY GRUP
          ═══════════════════════════════════════ */}
      <Section icon={<Target size={18} />} title="Grupy mięśniowe - co oznaczają nazwy">
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-6 gap-y-2 text-xs">
          {[
            ['Klatka piersiowa', 'Mięśnie pectoralis major - przód tułowia'],
            ['Przód barków', 'Przednia część mięśni naramiennych (deltoidów)'],
            ['Boki barków', 'Boczna część deltoidów - odpowiadają za szerokość ramion'],
            ['Tył barków', 'Tylna część deltoidów - ważne dla postawy'],
            ['Górny grzbiet', 'Mięśnie rhomboid i środkowy trapez - „grubość" pleców'],
            ['Najszersze (Lats)', 'Mięsień najszerszy grzbietu - „skrzydła", szerokość pleców'],
            ['Kaptur (Traps)', 'Mięsień czworoboczny - od karku do barków i łopatek'],
            ['Biceps', 'Mięsień dwugłowy ramienia - przód ramienia'],
            ['Triceps', 'Mięsień trójgłowy ramienia - tył ramienia (2/3 masy ramienia!)'],
            ['Przedramiona', 'Mięśnie podramienia - chwyt i ruchy nadgarstka'],
            ['Czworogłowy (Quads)', 'Mięsień czworogłowy uda - przód uda, prostowanie kolana'],
            ['Dwugłowy (Hamstrings)', 'Mięsień dwugłowy uda - tył uda, zginanie kolana'],
            ['Pośladki (Glutes)', 'Mięsień pośladkowy wielki - najsilniejszy mięsień ciała'],
            ['Łydki (Calves)', 'Mięsień trójgłowy łydki - prostowanie stopy'],
            ['Brzuch (Abs)', 'Mięsień prosty brzucha i skośne - stabilizacja, „sześciopak"'],
            ['Zginacze bioder', 'Mięsień biodrowo-lędźwiowy - podnoszenie nóg do góry'],
          ].map(([name, desc]) => (
            <div key={name} className="flex gap-2">
              <span className="font-bold text-slate-800 dark:text-slate-100 min-w-[120px]">{name}</span>
              <span className="text-slate-500 dark:text-slate-400">{desc}</span>
            </div>
          ))}
        </div>
      </Section>

      {/* ═══════════════════════════════════════
          11. TRENER AI
          ═══════════════════════════════════════ */}
      <Section icon={<BrainCircuit size={18} />} title="Trener AI - co oznaczają sugestie">
        <Term term="Brakujący wzorzec">
          Trener wykrył, że w twoim planie brakuje jednego ze wzorców ruchowych
          (np. brak ciągnięcia pionowego). Sugestia: dodaj ćwiczenie tego typu.
        </Term>
        <Divider />
        <Term term="Nierównowaga mięśniowa">
          Jedna partia ma dużo więcej serii niż antagonista (np. dużo klatki, mało pleców).
          Może prowadzić do wad postawy i kontuzji.
        </Term>
        <Divider />
        <Term term="Redundancja ćwiczeń">
          Masz kilka bardzo podobnych ćwiczeń w jednej sesji. Lepiej zamienić jedno na inny
          wzorzec dla lepszego pokrycia.
        </Term>
        <Divider />
        <Term term="Ryzyko zmęczenia">
          Za dużo ciężkiej pracy (wysokie RPE) bez odpowiedniego odpoczynku.
          Sugestia: zmniejsz RPE lub dodaj deload.
        </Term>
        <Divider />
        <Term term="Sugestia progresji">
          Dotyczy dopasowania modelu progresji do poziomu i celu trenującego.
        </Term>
      </Section>

      {/* ═══════════════════════════════════════
          12. SKRÓTY UŻYWANE W APLIKACJI
          ═══════════════════════════════════════ */}
      <Section icon={<BookOpen size={18} />} title="Spis skrótów">
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-8 gap-y-1.5 text-xs">
          {[
            ['RPE', 'Rating of Perceived Exertion - ocena trudności'],
            ['1RM', 'One Rep Max - jednopowtórzeniowe maximum'],
            ['%1RM', 'Procent jednopowtórzeniowego maximum'],
            ['MEV', 'Minimum Effective Volume - minimalna skuteczna objętość'],
            ['MAV', 'Maximum Adaptive Volume - optymalna objętość'],
            ['MRV', 'Maximum Recoverable Volume - max. objętość do regeneracji'],
            ['DUP', 'Daily Undulating Periodization - falowanie dzienne'],
            ['Serii/tydz.', 'Liczba serii na partię mięśniową w tygodniu'],
            ['×/tydz.', 'Sesji treningowych na tydzień'],
            ['Comp / Złożone', 'Ćwiczenie wielostawowe (compound)'],
            ['Iso / Izolacja', 'Ćwiczenie jednostawowe (isolation)'],
            ['Acc / Akces.', 'Ćwiczenie akcesoryjne / wspomagające'],
            ['Push', 'Ruch pchania (wyciskanie, pompki)'],
            ['Pull', 'Ruch ciągnięcia (wiosłowanie, podciąganie)'],
            ['Hinge', 'Ruch biodrowy (martwy ciąg)'],
            ['Deload', 'Tydzień zmniejszonej objętości/intens.'],
            ['AMRAP', 'As Many Reps As Possible - ile dasz rady'],
            ['Tempo', 'Format: ekscentryczna-pauza-koncentryczna-pauza (s)'],
          ].map(([abbr, desc]) => (
            <div key={abbr} className="flex gap-2">
              <span className="font-bold font-mono text-brand-600 dark:text-brand-400 min-w-[100px]">{abbr}</span>
              <span className="text-slate-500 dark:text-slate-400">{desc}</span>
            </div>
          ))}
        </div>
      </Section>

      {/* Footer */}
      <div className="text-center text-[10px] text-slate-400 pb-4 pt-2">
        APEX Builder - Legenda v1.0 · Wszystkie metryki oparte na aktualnej literaturze naukowej (Helms, Israetel, Schoenfeld)
      </div>
    </div>
  );
}

// ── Missing icon import alias ────────────────
function TrendingUp(props: { size: number }) {
  return (
    <svg
      xmlns="http://www.w3.org/2000/svg"
      width={props.size}
      height={props.size}
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
    >
      <polyline points="22 7 13.5 15.5 8.5 10.5 2 17" />
      <polyline points="16 7 22 7 22 13" />
    </svg>
  );
}
